import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../components/PageHeader';
import Badge from '../components/Badge';
import Pagination, { usePagination } from '../components/Pagination';
import {
  INVOICE_STATUS_TONE,
  filterInvoices,
  invoiceNetTotal,
  invoiceTotalJmd,
  isOverdue,
  listInvoices,
} from '../lib/invoices';
import {
  STOCKIST_STATUS_TONE,
  formatMonth,
  listStatements,
  listStockists,
  monthKey,
  sellThrough,
  statementTotal,
  statementUnits,
  statusOfStockist,
  totalsOf,
  unpaidDue,
} from '../lib/stockist';
import { formatJMD, formatUSD } from '../lib/finances';
import { todayISO } from '../lib/dates';
import './Invoices.css';

const FILTERS = ['All', 'Stockist', 'Direct', 'Payment due', 'Completed'];

// What to suggest when the list won't load. The hint used to be an
// unconditional "run migrations 0029 and 0030", which is only ever right for
// one class of failure and actively misleading for the rest — a rejected
// session token read as a missing table and sent you to the SQL editor.
// Postgres/PostgREST already say which kind of failure it is, so ask them.
function loadHint(err) {
  const code = err?.code || '';
  const text = `${err?.message || ''} ${err?.hint || ''}`;

  // Missing table or column: 42P01/42703 from Postgres, PGRST205/PGRST204 when
  // PostgREST's schema cache hasn't caught up with the DDL.
  if (['42P01', '42703', 'PGRST205', 'PGRST204'].includes(code)) {
    return "Run migrations 0029 and 0030 if you haven't yet, then `notify pgrst, 'reload schema';`.";
  }

  // The session, not the schema. PostgREST rejects the access token itself
  // (expired, not yet valid, signed with a rotated key).
  if (code === 'PGRST301' || /jwt|token/i.test(text)) {
    return 'That\u2019s your sign-in, not the data \u2014 sign out and back in to get a fresh session.';
  }

  return 'Reload the page; if it keeps happening, check the browser console.';
}

// The invoices list — the main screen of this section.
//
// It opens on what exists, not on a form: raising an invoice is occasional,
// while checking who's holding your stock and who owes you for what they sold
// is the daily question. Creating happens on its own route.
//
// The summary deliberately has no "Outstanding" card. Stock sitting in a shop
// is not money owed — it's inventory that happens to be elsewhere — and a
// figure that lumps it together with sold-but-unpaid money would overstate
// what's coming in, every month, by however much stock is out.
export default function Invoices() {
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [statementsByInvoice, setStatements] = useState({});
  const [stockists, setStockists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState('All');

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [invoiceRows, stockistRows] = await Promise.all([listInvoices(), listStockists()]);
      const stockistIds = invoiceRows.filter((i) => i.kind === 'Stockist').map((i) => i.id);
      const byInvoice = await listStatements(stockistIds);
      setInvoices(invoiceRows);
      setStockists(stockistRows);
      setStatements(byInvoice);
      setError(null);
    } catch (err) {
      setError(`Couldn't load invoices: ${err.message}. ${loadHint(err)}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const today = todayISO();

  // ---- the four figures ----
  const summary = useMemo(() => {
    const thisMonth = monthKey(today);
    let stockOutUnits = 0;
    let stockOutValue = 0;
    let dueFromSold = 0;
    let paidThisMonth = 0;
    const activeShops = new Set();

    for (const invoice of invoices) {
      if (invoice.kind === 'Stockist') {
        const statements = statementsByInvoice[invoice.id] || [];
        const status = statusOfStockist(invoice, statements, today);
        if (status !== 'Completed' && status !== 'Cancelled') {
          const totals = totalsOf(sellThrough(invoice, statements));
          stockOutUnits += totals.remaining;
          stockOutValue += totals.remainingValue;
          if (invoice.stockist_id) activeShops.add(invoice.stockist_id);
        }
        dueFromSold += unpaidDue(statements);
        for (const statement of statements) {
          if (statement.status === 'Paid' && statement.paid_on && monthKey(statement.paid_on) === thisMonth) {
            paidThisMonth += statementTotal(statement);
          }
        }
      } else if (invoice.status === 'Paid' && invoice.paid_at) {
        // Direct invoices count toward "paid this month" too — it's money in
        // from invoicing, regardless of which kind produced it.
        const paidDate = new Date(invoice.paid_at);
        if (monthKey(paidDate) === thisMonth) {
          paidThisMonth += invoiceTotalJmd(invoice, invoice.invoice_items);
        }
      }
    }

    return { stockOutUnits, stockOutValue, dueFromSold, paidThisMonth, activeShops: activeShops.size };
  }, [invoices, statementsByInvoice, today]);

  // ---- filtering ----
  const filtered = useMemo(() => {
    const base = filterInvoices(invoices, { query });
    if (filter === 'All') return base;
    if (filter === 'Direct') return base.filter((i) => i.kind !== 'Stockist');
    if (filter === 'Stockist') return base.filter((i) => i.kind === 'Stockist');
    return base.filter((invoice) => {
      if (invoice.kind === 'Stockist') {
        return statusOfStockist(invoice, statementsByInvoice[invoice.id] || [], today) === filter;
      }
      // The nearest direct-invoice equivalents of the two workflow filters.
      if (filter === 'Payment due') return invoice.status === 'Sent';
      return invoice.status === 'Paid';
    });
  }, [invoices, statementsByInvoice, query, filter, today]);

  const { visible, controls } = usePagination(filtered);

  return (
    <>
      <PageHeader
        title="Invoices"
        subtitle="Stock you've placed, what's sold, and what's been paid for."
        actions={
          <div className="page-header-buttons">
            <button className="ghost-button" onClick={() => navigate('/invoices/new')}>
              + New invoice
            </button>
            <button className="primary-action" onClick={() => navigate('/invoices/new-stockist')}>
              + New stockist invoice
            </button>
          </div>
        }
      />

      <div className="invoice-stats">
        <div className="invoice-stat">
          <span>Stock currently out</span>
          <strong>{formatJMD(summary.stockOutValue)}</strong>
          <em>{summary.stockOutUnits} units with stockists</em>
        </div>
        <div className="invoice-stat">
          <span>Payment due from sold stock</span>
          <strong>{formatJMD(summary.dueFromSold)}</strong>
          <em>Sold, not yet paid for</em>
        </div>
        <div className="invoice-stat">
          <span>Paid this month</span>
          <strong>{formatJMD(summary.paidThisMonth)}</strong>
          <em>In Accounting → Income</em>
        </div>
        <div className="invoice-stat">
          <span>Active stockists</span>
          <strong>{summary.activeShops}</strong>
          <em>of {stockists.length} shops</em>
        </div>
      </div>

      {error && <p className="po-error">{error}</p>}

      <section className="panel">
        <div className="invoice-toolbar">
          <input
            className="invoice-search"
            placeholder="Search by shop, customer, number, or product"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <div className="invoice-filters">
            {FILTERS.map((f) => (
              <button
                key={f}
                className={'batch-filter' + (filter === f ? ' active' : '')}
                onClick={() => setFilter(f)}
              >
                {f}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <p className="po-loading">Loading…</p>
        ) : filtered.length === 0 ? (
          <p className="po-empty">
            {invoices.length === 0
              ? 'No invoices yet. Send stock to a shop with “New stockist invoice”, or bill someone directly.'
              : 'No invoices match that.'}
          </p>
        ) : (
          <ul className="invoice-list">
            {visible.map((invoice) =>
              invoice.kind === 'Stockist' ? (
                <StockistRow
                  key={invoice.id}
                  invoice={invoice}
                  statements={statementsByInvoice[invoice.id] || []}
                  today={today}
                  onOpen={() => navigate(`/invoices/${invoice.id}`)}
                />
              ) : (
                <DirectRow
                  key={invoice.id}
                  invoice={invoice}
                  onOpen={() => navigate(`/invoices/${invoice.id}`)}
                />
              )
            )}
          </ul>
        )}

        <Pagination {...controls} />
      </section>
    </>
  );
}

// A consignment and its months, as one block. The children are shown inline
// rather than behind a click because the monthly rows *are* the state of the
// invoice — which month is owed is the thing you came to see.
function StockistRow({ invoice, statements, today, onOpen }) {
  const status = statusOfStockist(invoice, statements, today);
  const totals = totalsOf(sellThrough(invoice, statements));

  return (
    <li className="invoice-row stockist">
      <div className="stockist-parent" onClick={onOpen} role="button" tabIndex={0}
        onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onOpen()}>
        <div className="invoice-row-main">
          <div className="invoice-row-identity">
            <span className="invoice-row-number">{invoice.invoice_number}</span>
            <span className="invoice-row-name">{invoice.bill_to_name}</span>
            <Badge tone={STOCKIST_STATUS_TONE[status]}>{status}</Badge>
          </div>
          <span className="invoice-row-meta">
            <span>sent {invoice.issue_date}</span>
            <span>
              {totals.remaining} of {totals.sent} units still out
            </span>
            <span>{formatJMD(totals.soldValue)} sold to date</span>
          </span>
        </div>
        <span className="invoice-row-amount">
          {formatJMD(totals.remainingValue)}
          <em>stock out</em>
        </span>
      </div>

      {statements.length > 0 && (
        <ul className="statement-children">
          {statements.map((statement) => (
            <li key={statement.id}>
              <span className="statement-child-period">
                {formatMonth(statement.period_month)} sales
              </span>
              <span className="statement-child-units">{statementUnits(statement)} units</span>
              <span className="statement-child-amount">{formatJMD(statementTotal(statement))}</span>
              <Badge tone={statement.status === 'Paid' ? 'success' : 'warning'}>
                {statement.status}
              </Badge>
              <button className="statement-child-action" onClick={onOpen}>
                {statement.status === 'Paid' ? 'View' : 'Mark paid'}
              </button>
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}

function DirectRow({ invoice, onOpen }) {
  const money = invoice.currency === 'USD' ? formatUSD : formatJMD;
  const overdue = isOverdue(invoice);
  return (
    <li className={'invoice-row' + (overdue ? ' overdue' : '')}>
      <div className="stockist-parent" onClick={onOpen} role="button" tabIndex={0}
        onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onOpen()}>
        <div className="invoice-row-main">
          <div className="invoice-row-identity">
            <span className="invoice-row-number">{invoice.invoice_number}</span>
            <span className="invoice-row-name">{invoice.bill_to_name}</span>
            <Badge tone={INVOICE_STATUS_TONE[invoice.status]}>{invoice.status}</Badge>
            {overdue && <Badge tone="danger">Overdue</Badge>}
          </div>
          <span className="invoice-row-meta">
            <span>{invoice.issue_date}</span>
            <span>{(invoice.invoice_items || []).length} lines</span>
            {invoice.income_entry_id && <span className="invoice-row-booked">in Accounting</span>}
          </span>
        </div>
        <span className="invoice-row-amount">
          {money(invoiceNetTotal(invoice))}
          <em>direct invoice</em>
        </span>
      </div>
    </li>
  );
}
